import {
  EmptyData,
  P,
  Flex,
  FlexColumn,
  EmptyInput,
  InputRow,
  InputGroupBreak,
  ModalContext,
  AlertModalTypeEnum,
  ModelSelectSearchInput,
} from '@equifinance/shared';
import styled from 'styled-components';
import { useContext, useEffect, useMemo } from 'react';
import {
  useChecklistContext,
  ProductDecision,
  Status,
  SelectedProduct,
  useAccountContext,
  useProductsContext,
} from '../../../../contexts';
import { Info } from '../../../Products/Products';
import { ChecklistSection } from '../../ChecklistPage';
import { PaddedGenericSection } from '../GenericSection';
import { UnderwriterDecision } from '../../components/UnderwriterDecision';
import { Notes } from '../../components/Notes';
import { usePageConfigurationContext } from '../../../../contexts/pageConfiguration';

export const ProductRefName = 'product';

const BodyWrapper = styled(FlexColumn)`${({ theme }) => `
  padding: 0;
  gap: ${theme.padding[6]};
`}`;

const ProductsContainer = styled(Flex) <{ status: keyof typeof Status }>`
  ${({ theme, status }) => `
    gap: ${theme.padding[6]};
    flex-direction: ${status === 'Overridden' ? 'column' : 'column-reverse'};
`}`;

const InfoColumn = styled(Flex)`${({ theme }) => `
  width: 25%;

  &:first-child {
    width: ${theme.padding[12]};
  }
`}`;

const ProductBlock = styled(Flex)`${({ theme }) => `
  align-items: stretch;
  padding-inline: ${theme.padding[9]};
  border-radius: ${theme.padding[2]};
  background: ${theme.colours.neutral[9]};
`}`;

const EmptyDataContainer = styled.div`${({ theme }) => `
  padding-block: ${theme.padding[5]};
`}`;

const Label = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[2]};
  font-weight: ${theme.fontWeight.bold};
  padding-bottom: ${theme.padding[1]};
`}`;

const ProductBreak = styled(InputGroupBreak)`
  margin: 0;
`;

const InputRowBlock = styled(InputRow)`
  padding-block: 0;
`;

type ProductItemProps = {
  product: SelectedProduct;
};

function ProductItem(props: ProductItemProps) {
  const { product } = props;

  return (
    <ProductBlock>
      <InfoColumn>
        <Info
          id={`initial-pmt-${product?.id}`}
          label="Product code"
          value={product?.productCode}
          isHeading
        />
      </InfoColumn>
      <InfoColumn>
        <Info
          label="Initial pmt"
          prefix="£"
          value={product?.initialPayment}
          isHeading
          isFirstChild
        />
      </InfoColumn>
      <InfoColumn>
        <Info
          label="Product type"
          value={product?.type}
          isHeading
        />
      </InfoColumn>
      <InfoColumn>
        <Info
          label="Initial rate"
          value={product?.initialRate}
          isHeading
        />
      </InfoColumn>
      <InfoColumn />
      <InfoColumn />
    </ProductBlock>
  );
}

export default function ProductSection() {
  const {
    id, checklist, historicData, overrideProduct, updateChecklist,
  } = useChecklistContext();
  const { fetchProductsSummary } = useAccountContext();
  const { fetchProducts } = useProductsContext();
  const {
    setAlertModalProps, showAlertModal, closeAlertModal,
  } = useContext(ModalContext);
  const { isReadOnly } = usePageConfigurationContext();

  const { product, errors } = checklist ?? {};
  const { productReferences } = historicData ?? {};

  const underwriterDecisionOptions = {
    ...(product?.possibleDecisions?.reduce((options, decision) => ({
      ...options,
      [`${decision}`]: decision,
    }), {}) ?? {}),
    ...ProductDecision,
  };

  const productCodeOptions = useMemo(
    () => productReferences?.reduce((prev, { productCode }) => ({
      ...prev, [productCode]: productCode,
    }), {}) ?? [],
    [productReferences],
  );

  const onRemoveOverride = async () => {
    setAlertModalProps({
      isOpen: true,
      title: 'Product Override',
      contentHighlight: 'Are you sure you want to remove the overridden product?',
      content: '',
      confirmLabel: 'Confirm',
      type: AlertModalTypeEnum.WARNING_IRREVERSIBLE,
      hideClose: true,
      onClose: closeAlertModal,
      onConfirm: async () => {
        closeAlertModal();
        await overrideProduct({
          action: 'remove',
          productId: null,
        });
        fetchProductsSummary(id);
        fetchProducts();
      },
    });
    showAlertModal();
  };

  const onSubmitOverride = async () => {
    const productReference = productReferences.find(
      (item) => item.productCode === checklist.product.underwriterSelectedProduct.productCode,
    );
    if (!productReference) {
      return;
    }
    await overrideProduct({
      action: 'submit',
      productId: productReference.id,
    });
    fetchProductsSummary(id);
    fetchProducts();
  };

  const onStartOverride = async () => {
    await overrideProduct({
      action: 'start',
      productId: null,
    });
  };

  const onCancelOverride = () => {
    overrideProduct({
      action: 'cancel',
      productId: null,
    });
  };

  useEffect(() => {
    const uwSelectedProductError = errors?.[product.id]?.find((e) => e.propertyName === 'underwriterSelectedProduct');
    if (!isReadOnly && uwSelectedProductError && product.underwriterSelectedProduct == null) {
      updateChecklist('product.underwriterSelectedProduct.productCode', null);
    }
  }, [isReadOnly, errors]);

  return (
    <PaddedGenericSection
      refName={ProductRefName}
      section={ChecklistSection.PRODUCT}
      subSection={ChecklistSection.NOTES_PRODUCT}
      checklistItem={product}
      errors={checklist.errors}
      warnings={checklist.warnings}
    >
      <BodyWrapper>
        <ProductsContainer status={product?.status}>
          <div>
            {['Overridden', 'AwaitingDecision'].includes(product?.status) && (
              <>
                {product?.status === 'Overridden' && (
                  <>
                    <Label>Underwriter selected product:</Label>
                    <ProductItem product={product?.underwriterSelectedProduct} />
                  </>
                )}
                {product?.status === 'AwaitingDecision' && (
                  <InputRowBlock>
                    <ModelSelectSearchInput
                      label="Underwriter selected product:"
                      field="product.underwriterSelectedProduct.productCode"
                      placeholder="Please select"
                      options={productCodeOptions}
                      required={[null, 'Override'].includes(product?.decision)}
                      disabled={product?.checklistIndicatorType === 'Locked' || product?.submitted}
                    />
                    <EmptyInput />
                    <EmptyInput />
                  </InputRowBlock>
                )}
              </>
            )}
          </div>

          <ProductBreak />

          <div>
            <Label>Broker selected product:</Label>
            {product?.brokerSelectedProduct
              ? (
                <ProductItem product={product?.brokerSelectedProduct} />
              )
              : (
                <EmptyDataContainer>
                  <EmptyData message="Please start a new record." />
                </EmptyDataContainer>
              )}
          </div>
        </ProductsContainer>
        <UnderwriterDecision
          field="product"
          checklistItem={product}
          options={underwriterDecisionOptions}
          decisionDisabled
          submittedSectionDisabled={false}
          showPencilIcon={product?.status === 'AutoApproved'}
          showRemoveIcon={product?.status === 'Overridden'}
          onAction={(action) => (action === 'pencil' ? onStartOverride() : onRemoveOverride())}
          onCancel={onCancelOverride}
          onSubmit={onSubmitOverride}
        />

        {product?.overrideNotes?.length > 0 && (
          <Notes
            id={`${product?.id}-override-notes`}
            title="Override History"
            notesSection={ChecklistSection.OVERRIDE_NOTES_PRODUCT}
            notes={product?.overrideNotes}
          />
        )}
      </BodyWrapper>
    </PaddedGenericSection>
  );
}
